import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Cog, ChevronRight, Search, Filter, Factory, Droplets,
  Gauge, Zap, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Circle, Database
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { apiPumpManufacturers, apiPumpSeries, apiPumpModels } from '@/lib/phpApi';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface Manufacturer {
  id: string;
  name: string;
  country: string;
  website: string;
}

interface PumpSeries {
  id: string;
  manufacturerId: string;
  manufacturerName: string;
  name: string;
  pumpType: string;
  technology: string;
  maxPressureBar: number | null;
  maxFlowM3h: number | null;
}

interface PumpModel {
  id: string;
  seriesId: string;
  seriesName: string;
  manufacturerId: string;
  manufacturerName: string;
  code: string;
  motorKw: number | null;
  motorHp: number | null;
  speedRpm: string | null;
  casingMaterial: string | null;
  impellerMaterial: string | null;
  maxFlowM3h: number | null;
  maxPressureBar: number | null;
}

function getDafParams() {
  try {
    const raw = localStorage.getItem('daf-params-v2');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function getPumpCompatibility(
  model: PumpModel,
  requiredFlow: number,
  requiredPressure: number
): 'match' | 'small' | 'big' | 'unknown' {
  if (!model.maxFlowM3h || !model.maxPressureBar) return 'unknown';
  if (model.maxFlowM3h < requiredFlow * 0.9 || model.maxPressureBar < requiredPressure * 0.95) return 'small';
  if (model.maxFlowM3h > requiredFlow * 2.5) return 'big';
  return 'match';
}

export default function PumpListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [series, setSeries] = useState<PumpSeries[]>([]);
  const [models, setModels] = useState<PumpModel[]>([]);
  const [search, setSearch] = useState('');
  const [filterMfr, setFilterMfr] = useState<string>('all');
  const [filterSeries, setFilterSeries] = useState<string>('all');

  const dafParams = getDafParams();
  const requiredFlow = dafParams ? (dafParams.flowRate / 24) * (dafParams.recycleRate / 100) : 0;
  const requiredPressure = dafParams ? dafParams.saturatorPressure : 0;
  const selectedPumpId = dafParams?.selectedPumpId as string | undefined;

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [mfrRes, seriesRes, modelsRes] = await Promise.all([
        apiPumpManufacturers(),
        apiPumpSeries(),
        apiPumpModels(),
      ]);
      setManufacturers(mfrRes.manufacturers || []);
      setSeries(seriesRes.series || []);
      setModels(modelsRes.models || []);
    } catch (err) {
      toast.error('Failed to load pump database');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const filteredModels = useMemo(() => {
    const filtered = models.filter(m => {
      const matchesSearch = !search ||
        m.code.toLowerCase().includes(search.toLowerCase()) ||
        m.manufacturerName.toLowerCase().includes(search.toLowerCase()) ||
        m.seriesName.toLowerCase().includes(search.toLowerCase());
      const matchesMfr = filterMfr === 'all' || m.manufacturerId === filterMfr;
      const matchesSeries = filterSeries === 'all' || m.seriesId === filterSeries;
      return matchesSearch && matchesMfr && matchesSeries;
    });
    // Sort: selected first, then recommended, then others
    return filtered.sort((a, b) => {
      if (a.id === selectedPumpId) return -1;
      if (b.id === selectedPumpId) return 1;
      const compA = getPumpCompatibility(a, requiredFlow, requiredPressure);
      const compB = getPumpCompatibility(b, requiredFlow, requiredPressure);
      const score = { match: 0, big: 1, unknown: 2, small: 3 };
      return score[compA] - score[compB];
    });
  }, [models, search, filterMfr, filterSeries, requiredFlow, requiredPressure, selectedPumpId]);

  const availableSeries = useMemo(() => {
    if (filterMfr === 'all') return series;
    return series.filter(s => s.manufacturerId === filterMfr);
  }, [series, filterMfr]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-5 max-w-[1440px] mx-auto"
    >
        {/* Breadcrumb + Header */}
        <div className="flex flex-col gap-3">
          <button
            onClick={() => navigate('/daf-spec')}
            className="self-start flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800/60 text-slate-400 border border-slate-800/50 hover:text-sky-400 hover:border-sky-500/30 hover:bg-slate-800 transition-all"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to DAF Specification
          </button>
          <PageHeader
            icon={Database}
            iconColor="text-sky-400"
            title="DAF Pump Database"
            subtitle="Pressure-rated pump specifications for DAF recycle systems"
            badge={
              selectedPumpId && dafParams?.selectedPumpCode ? (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-yellow-500/5 border border-yellow-500/20">
                  <CheckCircle2 className="w-3.5 h-3.5 text-yellow-400" />
                  <span className="text-xs text-yellow-400 font-medium">{dafParams.selectedPumpCode}</span>
                  <span className="text-xs text-slate-500">selected for DAF</span>
                </div>
              ) : undefined
            }
            actions={
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Droplets className="w-3.5 h-3.5 text-sky-400" />
                <span>OEM datasheets & peer-reviewed literature</span>
              </div>
            }
          />
        </div>

        {/* Filters */}
        <Card className="bg-rb-surface border-slate-800/50">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search by model, series, manufacturer..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-slate-800/60 border-slate-700 text-slate-200 placeholder:text-slate-500"
                />
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <Factory className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <select
                    value={filterMfr}
                    onChange={(e) => { setFilterMfr(e.target.value); setFilterSeries('all'); }}
                    className="pl-9 pr-8 py-2 rounded-md bg-slate-800/60 border border-slate-700 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  >
                    <option value="all">All Manufacturers</option>
                    {manufacturers.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div className="relative">
                  <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <select
                    value={filterSeries}
                    onChange={(e) => setFilterSeries(e.target.value)}
                    className="pl-9 pr-8 py-2 rounded-md bg-slate-800/60 border border-slate-700 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-sky-500"
                  >
                    <option value="all">All Series</option>
                    {availableSeries.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pump Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-40 bg-slate-800/60" />
            ))}
          </div>
        ) : filteredModels.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Cog className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg font-medium">No pumps match your filters</p>
            <p className="text-sm mt-1">Try adjusting your search criteria</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredModels.map((model, idx) => {
              const isSelected = selectedPumpId === model.id;
              const compat = getPumpCompatibility(model, requiredFlow, requiredPressure);
              const compatBadge = {
                match: { text: 'Matches DAF', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle2 },
                small: { text: 'Too small', color: 'bg-rose-500/10 text-rose-400 border-rose-500/20', icon: AlertTriangle },
                big: { text: 'Too big', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: AlertTriangle },
                unknown: { text: 'Check specs', color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: Circle },
              }[compat];
              const CompatIcon = compatBadge.icon;
              return (
                <motion.div
                  key={model.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                >
                  <Card
                    className={`relative bg-rb-surface border-slate-800/50 hover:border-sky-500/40 hover:bg-slate-800/60 transition-all cursor-pointer group ${isSelected ? 'ring-1 ring-yellow-400/30' : ''}`}
                    onClick={() => navigate(`/pumps/${model.id}`)}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <CheckCircle2 className="w-5 h-5 text-yellow-400" />
                      </div>
                    )}
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-400">
                              {model.manufacturerName}
                            </Badge>
                            {requiredFlow > 0 && (
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${compatBadge.color}`}>
                                <CompatIcon className="w-3 h-3" />
                                {compatBadge.text}
                              </span>
                            )}
                          </div>
                          <h3 className="text-lg font-bold text-white group-hover:text-sky-300 transition-colors">
                            {model.code}
                          </h3>
                          <p className="text-xs text-slate-400">{model.seriesName}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-sky-400 transition-colors mt-1" />
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        {model.motorKw !== null && (
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Zap className="w-3.5 h-3.5 text-amber-400" />
                            <span>{model.motorKw} kW</span>
                          </div>
                        )}
                        {model.motorHp !== null && (
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Gauge className="w-3.5 h-3.5 text-emerald-400" />
                            <span>{model.motorHp} HP</span>
                          </div>
                        )}
                        {model.speedRpm && (
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Cog className="w-3.5 h-3.5 text-slate-500" />
                            <span>{model.speedRpm}</span>
                          </div>
                        )}
                        {model.casingMaterial && (
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Factory className="w-3.5 h-3.5 text-slate-500" />
                            <span className="truncate">{model.casingMaterial}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-3 flex items-center gap-1 text-xs text-sky-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        View curves & specs <ArrowRight className="w-3 h-3" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
    </motion.div>
  );
}
